/** FBS finding status v2 — aligned with getscanned `fbsFindingStatuses.js`. */

export interface FbsV2Status {
  key: string;
  label: string;
  description: string;
  className: string;
  variant: 'red' | 'orange' | 'yellow' | 'blue' | 'green';
  color: string;
  bgColor: string;
}

export interface FbsReportStatusItem {
  status?: string;
  statusKey?: string;
  newStatus?: string;
  new_status?: string;
  oldStatus?: string;
  old_status?: string;
  detailedNote?: string | null;
  detailed_note?: string | null;
}

export const FBS_FINDING_STATUS_V2_LIST: FbsV2Status[] = [
  {
    key: 'critical_finding',
    label: 'Critical finding',
    description: 'Needs urgent medical review.',
    className: 'statusTagCriticalFinding',
    variant: 'red',
    color: '#B13D3D',
    bgColor: '#EEC8C8'
  },
  {
    key: 'significant_actionable_finding',
    label: 'Significant finding',
    description: 'An important finding that needs planned follow-up.',
    className: 'statusTagSignificantActionableFinding',
    variant: 'orange',
    color: '#DB8D21',
    bgColor: '#FADFB6'
  },
  {
    key: 'indeterminate_finding',
    label: 'Inconclusive finding',
    description: 'Probably benign, but whole-body MRI cannot fully characterise it.',
    className: 'statusTagIndeterminateFinding',
    variant: 'yellow',
    color: '#856404',
    bgColor: '#FFF3CD'
  },
  {
    key: 'benign_incidental_finding',
    label: 'Benign finding',
    description: 'A common harmless finding that usually does not need treatment or follow-up.',
    className: 'statusTagBenignIncidentalFinding',
    variant: 'blue',
    color: '#0066CC',
    bgColor: '#B3D9FF'
  },
  {
    key: 'no_significant_abnormality',
    label: 'No findings',
    description: 'No concerning abnormality was seen.',
    className: 'statusTagNoSignificantAbnormality',
    variant: 'green',
    color: '#438F51',
    bgColor: '#D0E5D4'
  },
  {
    key: 'not_reported',
    label: 'Not reported',
    description: 'This finding has not yet been reported.',
    className: 'statusTagNotReported',
    variant: 'green',
    color: '#6A6A6C',
    bgColor: '#E2E2E5'
  }
];

const v2ByKey = new Map(FBS_FINDING_STATUS_V2_LIST.map((s) => [s.key, s]));
const v2ByLabel = new Map(FBS_FINDING_STATUS_V2_LIST.map((s) => [s.label, s]));

export const FBS_NO_FINDINGS_LABELS = ['No findings', 'No significant abnormality'];

const LEGACY_LABEL_TO_V2_KEY: Record<string, string> = {
  'Important finding detected': 'critical_finding',
  'Incidental findings - Assess': 'significant_actionable_finding',
  'Seen, not clinically significant': 'benign_incidental_finding',
  'No abnormality detected': 'no_significant_abnormality',
  'Not reported': 'not_reported'
};

const PREVIOUS_V2_LABEL_TO_KEY: Record<string, string> = {
  'Significant actionable finding': 'significant_actionable_finding',
  'Indeterminate finding': 'indeterminate_finding',
  'Benign incidental finding': 'benign_incidental_finding',
  'No significant abnormality': 'no_significant_abnormality'
};

const V2_KEY_TO_LEGACY_LABEL: Record<string, string> = {
  critical_finding: 'Important finding detected',
  significant_actionable_finding: 'Incidental findings - Assess',
  indeterminate_finding: 'Incidental findings - Assess',
  benign_incidental_finding: 'Seen, not clinically significant',
  no_significant_abnormality: 'No abnormality detected',
  not_reported: 'Not reported'
};

const KNOWN_LEGACY_STATUS_LABELS = new Set(Object.values(V2_KEY_TO_LEGACY_LABEL));

const FBS_V2_ABNORMAL_KEYS = new Set([
  'critical_finding',
  'significant_actionable_finding',
  'indeterminate_finding',
  'benign_incidental_finding'
]);

export const FBS_CONSOLIDATED_FINDING_FILTERS_V2 = [
  { id: 'critical_finding', label: 'Critical finding', className: 'statusTagCriticalFinding' },
  {
    id: 'significant_actionable_finding',
    label: 'Significant finding',
    className: 'statusTagSignificantActionableFinding'
  },
  {
    id: 'indeterminate_finding',
    label: 'Inconclusive finding',
    className: 'statusTagIndeterminateFinding'
  },
  {
    id: 'benign_incidental_finding',
    label: 'Benign finding',
    className: 'statusTagBenignIncidentalFinding'
  }
];

export const CONSOLIDATED_FILTER_TAB_STYLES: Record<string, { color: string; bg: string; ring: string }> = {
  'Important finding detected': { color: '#B13D3D', bg: '#EEC8C8', ring: '#EFD8D8' },
  'Incidental findings - Assess': { color: '#DB8D21', bg: '#FADFB6', ring: '#FADFB6' },
  'Seen, not clinically significant': { color: '#0066CC', bg: '#B3D9FF', ring: '#E7F0FF' },
  critical_finding: { color: '#B13D3D', bg: '#EEC8C8', ring: '#EFD8D8' },
  significant_actionable_finding: { color: '#DB8D21', bg: '#FADFB6', ring: '#FADFB6' },
  indeterminate_finding: { color: '#856404', bg: '#FFF3CD', ring: '#FFF3CD' },
  benign_incidental_finding: { color: '#0066CC', bg: '#B3D9FF', ring: '#E7F0FF' }
};

export const getConsolidatedFilterTabStyles = (filterId: string) =>
  CONSOLIDATED_FILTER_TAB_STYLES[filterId] || {
    color: '#6A6A6C',
    bg: '#E2E2E5',
    ring: '#F5F5F7'
  };

export const resolveFbsV2Display = ({
  statusKey,
  statusLabel,
  status
}: {
  statusKey?: string;
  statusLabel?: string;
  status?: string;
}): FbsV2Status => {
  if (statusKey && v2ByKey.has(statusKey)) {
    return v2ByKey.get(statusKey)!;
  }
  const label = statusLabel || status;
  if (label && v2ByLabel.has(label)) {
    return v2ByLabel.get(label)!;
  }
  if (label && LEGACY_LABEL_TO_V2_KEY[label]) {
    return v2ByKey.get(LEGACY_LABEL_TO_V2_KEY[label])!;
  }
  if (label && PREVIOUS_V2_LABEL_TO_KEY[label]) {
    return v2ByKey.get(PREVIOUS_V2_LABEL_TO_KEY[label])!;
  }
  return v2ByKey.get('no_significant_abnormality')!;
};

export const getEffectiveLegacyLabel = (item: FbsReportStatusItem = {}): string =>
  item.status || item.oldStatus || item.old_status || 'Not reported';

export const resolveLegacyStatusLabel = (label?: string): string => {
  const normalized = label || 'Not reported';
  if (KNOWN_LEGACY_STATUS_LABELS.has(normalized)) return normalized;

  const display = resolveFbsV2Display({ statusLabel: normalized, status: normalized });
  return V2_KEY_TO_LEGACY_LABEL[display.key] || normalized;
};

export const getConsolidatedLegacyFilterId = (item: FbsReportStatusItem = {}): string =>
  resolveLegacyStatusLabel(getEffectiveLegacyLabel(item));

export const isFbsV2AbnormalKey = (key?: string): boolean =>
  Boolean(key && FBS_V2_ABNORMAL_KEYS.has(key));

export const gpStatusReviewCompleted = (booking: Record<string, unknown> = {}): boolean =>
  Boolean(
    booking.fbsFindingStatusReviewCompletedAt || booking.fbs_finding_status_review_completed_at
  );

const V2_STATUS_KEYS = new Set(FBS_FINDING_STATUS_V2_LIST.map((s) => s.key));

/** API / booking payload flag — explicit true/false when present, else null. */
export const resolveExplicitFbsV2Flag = (
  booking: Record<string, unknown> = {}
): boolean | null => {
  if (gpStatusReviewCompleted(booking)) return true;
  if (booking.usesFbsFindingStatusV2 === true || booking.uses_fbs_finding_status_v2 === true) {
    return true;
  }
  if (booking.usesFbsFindingStatusV2 === false || booking.uses_fbs_finding_status_v2 === false) {
    return false;
  }
  return null;
};

export const bookingUsesFbsV2 = (booking: Record<string, unknown> = {}): boolean =>
  resolveExplicitFbsV2Flag(booking) === true;

type ReportDataLike = Record<
  string,
  {
    status?: string;
    organs?: Record<string, { items?: FbsReportStatusItem[] }>;
  }
>;

const itemHasPersistedV2Key = (item: FbsReportStatusItem): boolean => {
  const key = item.statusKey || item.newStatus || item.new_status;
  return Boolean(key && V2_STATUS_KEYS.has(key));
};

const labelIsV2Only = (label?: string): boolean => {
  if (!label) return false;
  if (v2ByLabel.has(label)) return true;
  return Boolean(PREVIOUS_V2_LABEL_TO_KEY[label]);
};

/** Legacy-era finding labels (exclude neutral "Not reported"). */
const LEGACY_ERA_LABELS = new Set([
  'Important finding detected',
  'Incidental findings - Assess',
  'Seen, not clinically significant',
  'No abnormality detected'
]);

const labelIndicatesLegacyEra = (label?: string): boolean => {
  if (!label) return false;
  return LEGACY_ERA_LABELS.has(label);
};

export type ReportStatusEra = 'legacy' | 'v2' | 'unknown';

/** Classify report rows as legacy v1 labels, v2 labels/keys, or inconclusive. */
export const detectReportStatusEra = (reportData: ReportDataLike = {}): ReportStatusEra => {
  const systems = Object.values(reportData);
  if (systems.length === 0) return 'unknown';

  let hasOldStatusSnapshot = false;
  let hasV2Signal = false;
  let hasLegacyLabel = false;

  const inspectLabel = (label?: string) => {
    if (labelIsV2Only(label)) hasV2Signal = true;
    if (labelIndicatesLegacyEra(label)) hasLegacyLabel = true;
  };

  for (const system of systems) {
    inspectLabel(system.status);

    for (const organ of Object.values(system.organs || {})) {
      for (const item of organ.items || []) {
        if (item.oldStatus || item.old_status) {
          hasOldStatusSnapshot = true;
        }
        if (itemHasPersistedV2Key(item)) {
          hasV2Signal = true;
        }
        inspectLabel(item.status);
      }
    }
  }

  // After GP review, rows keep old_status snapshots alongside v2 new_status keys.
  if (hasV2Signal && hasOldStatusSnapshot && !hasLegacyLabel) return 'v2';
  if (hasOldStatusSnapshot || hasLegacyLabel) return 'legacy';
  if (hasV2Signal) return 'v2';
  return 'unknown';
};

/** Fallback when the API omits `uses_fbs_finding_status_v2`. */
export const inferUsesFbsFindingStatusV2FromReportData = (
  reportData: ReportDataLike = {}
): boolean => detectReportStatusEra(reportData) === 'v2';

export const itemMatchesConsolidatedFindingFilter = (
  item: FbsReportStatusItem,
  filterId: string,
  bookingUsesV2 = false
): boolean => {
  if (!item || !filterId) return false;

  if (bookingUsesV2) {
    const itemKey = item.statusKey || item.newStatus || item.new_status;
    if (itemKey === filterId) return true;
    const display = resolveFbsV2Display({
      statusKey: itemKey,
      statusLabel: item.status,
      status: item.status
    });
    return display.key === filterId;
  }

  return getConsolidatedLegacyFilterId(item) === filterId;
};

export const shouldShowConsolidatedCondition = (
  item: FbsReportStatusItem,
  bookingUsesV2 = false
): boolean => {
  const hasNote = Boolean(
    (typeof item.detailedNote === 'string' && item.detailedNote.trim()) ||
      (typeof item.detailed_note === 'string' && item.detailed_note.trim())
  );

  if (bookingUsesV2) {
    const itemKey = item.statusKey || item.newStatus || item.new_status;
    const display = resolveFbsV2Display({
      statusKey: itemKey,
      statusLabel: item.status,
      status: item.status
    });
    const resolvedKey = display.key;

    if (isFbsV2AbnormalKey(resolvedKey)) return true;
    if ((resolvedKey === 'no_significant_abnormality' || resolvedKey === 'not_reported') && hasNote) {
      return true;
    }
    return false;
  }

  const status = getEffectiveLegacyLabel(item);
  const isNoFindingStatus =
    status === 'No abnormality detected' || FBS_NO_FINDINGS_LABELS.includes(status);

  const v2Display = resolveFbsV2Display({ statusLabel: status, status });
  if (isFbsV2AbnormalKey(v2Display.key)) return true;

  if (
    status === 'Important finding detected' ||
    status === 'Incidental findings - Assess' ||
    status === 'Seen, not clinically significant'
  ) {
    return true;
  }
  if (isNoFindingStatus) return hasNote;
  if (hasNote) return true;
  return false;
};

const LEGACY_STATUS_COLORS: Record<string, { color: string; bgColor: string }> = {
  'Not reported': { color: '#6A6A6C', bgColor: '#E2E2E5' },
  'No abnormality detected': { color: '#438F51', bgColor: '#D0E5D4' },
  'Incidental findings - Assess': { color: '#DB8D21', bgColor: '#FADFB6' },
  'Important finding detected': { color: '#B13D3D', bgColor: '#EEC8C8' },
  'Seen, not clinically significant': { color: '#0066CC', bgColor: '#B3D9FF' }
};

export const getStatusDisplayForItem = (item: FbsReportStatusItem = {}, bookingUsesV2 = false) => {
  if (bookingUsesV2) {
    const display = resolveFbsV2Display({
      statusKey: item.statusKey || item.newStatus || item.new_status,
      statusLabel: item.status,
      status: item.status
    });
    return {
      label: display.label,
      className: display.className,
      variant: display.variant,
      color: display.color,
      bgColor: display.bgColor
    };
  }

  const label = getEffectiveLegacyLabel(item);
  const legacyColors = LEGACY_STATUS_COLORS[label] || LEGACY_STATUS_COLORS['Not reported'];
  const legacyClassMap: Record<string, string> = {
    'Not reported': 'statusTagNotReported',
    'No abnormality detected': 'statusTagNoAbnormalityDetected',
    'Incidental findings - Assess': 'statusTagIncidentalFindingsAssess',
    'Important finding detected': 'statusTagImportantFindingDetected',
    'Seen, not clinically significant': 'statusTagSeenNotClinicallySignificant'
  };

  return {
    label,
    className: legacyClassMap[label] || 'statusTagNotReported',
    variant: null as string | null,
    color: legacyColors.color,
    bgColor: legacyColors.bgColor
  };
};

export const getSystemStatusDisplay = (
  system: { status?: string },
  bookingUsesV2 = false
) => {
  const rawLabel = system?.status || 'Not reported';

  if (bookingUsesV2) {
    const display = resolveFbsV2Display({ statusLabel: rawLabel, status: rawLabel });
    return {
      label: display.label,
      className: display.className,
      variant: display.variant,
      color: display.color,
      bgColor: display.bgColor
    };
  }

  const label = resolveLegacyStatusLabel(rawLabel);
  const legacyColors = LEGACY_STATUS_COLORS[label] || LEGACY_STATUS_COLORS['Not reported'];
  const legacyClassMap: Record<string, string> = {
    'Not reported': 'statusTagNotReported',
    'No abnormality detected': 'statusTagNoAbnormalityDetected',
    'Incidental findings - Assess': 'statusTagIncidentalFindingsAssess',
    'Important finding detected': 'statusTagImportantFindingDetected',
    'Seen, not clinically significant': 'statusTagSeenNotClinicallySignificant'
  };

  return {
    label,
    className: legacyClassMap[label] || 'statusTagNotReported',
    variant: null as string | null,
    color: legacyColors.color,
    bgColor: legacyColors.bgColor
  };
};
